import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Check, X, Sparkles, Shield, Users, Globe, Zap, 
  ArrowRight, Loader2, AlertCircle, CheckCircle2, 
  Package, Star, Clock, CreditCard, Headphones
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

const TIER_COLORS = {
  tier1: 'from-blue-500 to-blue-600',
  tier2: 'from-purple-500 to-purple-600',
  tier3: 'from-amber-500 to-orange-600'
};

const TIER_BADGES = {
  tier1: 'Starter',
  tier2: 'Growth',
  tier3: 'Scale'
};

export default function AppSumoLanding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get license key from URL if provided
  const urlLicenseKey = searchParams.get('license_key') || searchParams.get('code');
  
  const [step, setStep] = useState('validate'); // validate, signup, success
  const [licenseKey, setLicenseKey] = useState(urlLicenseKey || '');
  const [validating, setValidating] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [error, setError] = useState(null);
  const [tiers, setTiers] = useState([]);
  
  // Signup form
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    company_name: '',
    password: '',
    confirm_password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [activationResult, setActivationResult] = useState(null);

  // Load tiers on mount
  useEffect(() => {
    loadTiers();
    
    // Auto-validate if license key in URL
    if (urlLicenseKey) {
      validateLicense(urlLicenseKey);
    }
  }, []);

  const loadTiers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/appsumo/tiers`);
      if (response.ok) {
        const data = await response.json();
        setTiers(data.tiers || []);
      }
    } catch (err) {
      console.error('Failed to load tiers:', err);
    }
  };

  const validateLicense = async (key = licenseKey) => {
    if (!key.trim()) {
      setError('Please enter a license key');
      return;
    }
    
    setValidating(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/appsumo/validate/${key.trim()}`);
      const data = await response.json();
      
      if (data.valid) {
        setLicenseInfo(data);
        setStep('signup');
      } else {
        setError(data.error || 'Invalid license key');
      }
    } catch (err) {
      setError('Failed to validate license key');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/appsumo/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          license_key: licenseKey,
          email: formData.email,
          full_name: formData.full_name,
          company_name: formData.company_name,
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setActivationResult(data);
        setStep('success');
        toast.success('Account activated successfully!');
      } else {
        toast.error(data.detail || 'Failed to activate license');
      }
    } catch (err) {
      toast.error('Failed to activate license');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFeature = (text, included) => (
    <li className="flex items-center gap-2">
      {included ? (
        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
      ) : (
        <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
      )}
      <span className={included ? 'text-slate-700' : 'text-slate-400'}>{text}</span>
    </li>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">W</span>
            </div>
            <span className="text-2xl font-bold text-white">WPManage</span>
          </Link>
          <Badge className="bg-amber-500 text-white">
            <Sparkles className="w-4 h-4 mr-1" />
            AppSumo Exclusive
          </Badge>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-12 text-center">
        <Badge className="bg-green-500/20 text-green-400 mb-6 text-sm px-4 py-2">
          🎉 Lifetime Deal - No Monthly Fees Ever!
        </Badge>
        
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
          White-Label Client Portal<br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
            for WordPress Agencies
          </span>
        </h1>
        
        <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
          Give your clients a branded portal to manage services, view invoices, 
          access knowledge base, and get support. Fully white-labeled for your agency.
        </p>

        <div className="flex justify-center gap-4 flex-wrap mb-12">
          <div className="flex items-center gap-2 text-slate-300">
            <Shield className="w-5 h-5 text-green-400" />
            <span>Lifetime Access</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Users className="w-5 h-5 text-blue-400" />
            <span>Unlimited Clients</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Globe className="w-5 h-5 text-purple-400" />
            <span>Custom Domain</span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Zap className="w-5 h-5 text-amber-400" />
            <span>Instant Setup</span>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="container mx-auto px-4 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left: Activation Form */}
          <div>
            {step === 'validate' && (
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">Redeem Your License</CardTitle>
                  <CardDescription className="text-slate-300">
                    Enter your AppSumo license key to get started
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="license" className="text-slate-200">License Key</Label>
                    <Input
                      id="license"
                      placeholder="APPSUMO-XXXX-XXXX-XXXX"
                      value={licenseKey}
                      onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      data-testid="license-key-input"
                    />
                  </div>
                  
                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-300">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  
                  <Button 
                    onClick={() => validateLicense()}
                    disabled={validating}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                    size="lg"
                    data-testid="validate-license-btn"
                  >
                    {validating ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5 mr-2" />
                    )}
                    Validate License
                  </Button>
                </CardContent>
              </Card>
            )}

            {step === 'signup' && licenseInfo && (
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                    <Badge className={`bg-gradient-to-r ${TIER_COLORS[licenseInfo.tier]} text-white`}>
                      {TIER_BADGES[licenseInfo.tier]} Plan
                    </Badge>
                  </div>
                  <CardTitle className="text-white text-2xl">Create Your Account</CardTitle>
                  <CardDescription className="text-slate-300">
                    Complete your registration to activate the {licenseInfo.tier_name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="full_name" className="text-slate-200">Full Name *</Label>
                        <Input
                          id="full_name"
                          required
                          value={formData.full_name}
                          onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          data-testid="full-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-slate-200">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="company_name" className="text-slate-200">Company/Agency Name</Label>
                      <Input
                        id="company_name"
                        placeholder="Your Agency Name"
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        data-testid="company-name-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-slate-200">Password *</Label>
                        <Input
                          id="password"
                          type="password"
                          required
                          minLength={8}
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          data-testid="password-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm_password" className="text-slate-200">Confirm Password *</Label>
                        <Input
                          id="confirm_password"
                          type="password"
                          required
                          value={formData.confirm_password}
                          onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          data-testid="confirm-password-input"
                        />
                      </div>
                    </div>
                    
                    <Button 
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                      size="lg"
                      data-testid="activate-account-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                      )}
                      Activate My Account
                    </Button>
                    
                    <button
                      type="button"
                      onClick={() => { setStep('validate'); setLicenseInfo(null); }}
                      className="w-full text-slate-400 hover:text-white text-sm"
                    >
                      ← Use a different license key
                    </button>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === 'success' && activationResult && (
              <Card className="bg-white/10 backdrop-blur-lg border-green-500/30 border-2">
                <CardContent className="pt-8 text-center">
                  <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  </div>
                  
                  <h2 className="text-3xl font-bold text-white mb-2">Welcome Aboard! 🎉</h2>
                  <p className="text-slate-300 mb-6">
                    Your {activationResult.tenant?.tier_name} account has been activated.
                  </p>
                  
                  <div className="bg-white/5 rounded-lg p-4 mb-6 text-left">
                    <p className="text-slate-400 text-sm mb-1">Your Portal</p>
                    <p className="text-white font-semibold text-lg">{activationResult.tenant?.name}</p>
                    <p className="text-slate-400 text-sm">Slug: {activationResult.tenant?.slug}</p>
                  </div>
                  
                  <Button 
                    onClick={() => navigate('/login')}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                    size="lg"
                  >
                    Go to Login
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Tier Comparison */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white mb-4">What's Included</h2>
            
            {tiers.map((tier) => (
              <Card 
                key={tier.id}
                className={`bg-white/5 backdrop-blur-sm border-white/10 ${
                  licenseInfo?.tier === tier.id ? 'ring-2 ring-amber-500' : ''
                }`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${TIER_COLORS[tier.id]} flex items-center justify-center`}>
                        <Star className="w-4 h-4 text-white" />
                      </div>
                      <CardTitle className="text-white">{tier.name}</CardTitle>
                    </div>
                    {licenseInfo?.tier === tier.id && (
                      <Badge className="bg-amber-500 text-white">Your Plan</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Users className="w-4 h-4" />
                        <span>{tier.limits.max_clients} Clients</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Package className="w-4 h-4" />
                        <span>{tier.limits.max_services} Services</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Users className="w-4 h-4" />
                        <span>{tier.limits.max_team_members} Team Members</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Globe className="w-4 h-4" />
                        <span>{tier.limits.max_domains} Custom Domain{tier.limits.max_domains > 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-300">
                        <CreditCard className="w-4 h-4" />
                        <span>{tier.platform_fee}% Platform Fee</span>
                      </div>
                      {tier.features.white_label && (
                        <div className="flex items-center gap-2 text-green-400">
                          <Check className="w-4 h-4" />
                          <span>White Label</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Features List */}
            <Card className="bg-white/5 backdrop-blur-sm border-white/10">
              <CardHeader>
                <CardTitle className="text-white">All Plans Include</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-slate-300">
                  {renderFeature('Branded Client Portal', true)}
                  {renderFeature('Service Management', true)}
                  {renderFeature('Invoice & Billing', true)}
                  {renderFeature('Knowledge Base', true)}
                  {renderFeature('Support Ticketing', true)}
                  {renderFeature('Email Notifications', true)}
                  {renderFeature('Stripe Integration', true)}
                  {renderFeature('SSL Certificate', true)}
                  {renderFeature('Lifetime Updates', true)}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-white/10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-slate-400 text-sm">
          <div className="flex items-center gap-2">
            <Headphones className="w-4 h-4" />
            <span>Need help? Contact support@wpmanage.io</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
